import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  KeyRound, Eye, EyeOff, Loader2, CheckCircle2, ShieldCheck, X,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Change-password card for logged-in users. Accessible from Profile pages
 * for both managers and workers.
 *
 * Backend: PATCH /api/users/me/password { old_password, new_password }
 * - Validates current password
 * - Enforces ≥8 chars + different from current
 * - Sends Resend confirmation email on success
 */
export default function ChangePasswordCard() {
  const { api } = useAuth();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const strength = computeStrength(form.next);
  const matches = form.next.length > 0 && form.next === form.confirm;
  const canSubmit =
    form.current.length > 0 &&
    form.next.length >= 8 &&
    matches &&
    form.next !== form.current &&
    !saving;

  const handleChange = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    if (error) setError(null);
    if (success) setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      await api.patch('/users/me/password', {
        old_password: form.current,
        new_password: form.next,
      });
      setSuccess(true);
      setForm({ current: '', next: '', confirm: '' });
      toast.success('Password updated — confirmation email sent.');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not change password';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border border-slate-200" data-testid="change-password-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start gap-3 mb-5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0B132B]/5 shrink-0">
            <KeyRound className="h-4 w-4 text-[#0B132B]" strokeWidth={1.75} />
          </span>
          <div className="min-w-0">
            <h3 className="text-[15px] font-bold text-slate-900">Change password</h3>
            <p className="text-[13px] text-slate-500 mt-0.5 leading-relaxed">
              Update your sign-in password. You'll receive an email confirmation once changed.
            </p>
          </div>
        </div>

        {success && (
          <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 p-3.5" data-testid="password-success">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
            <p className="text-[13px] text-emerald-900 leading-relaxed">
              Password updated. A confirmation email has been sent.
            </p>
          </div>
        )}
        {error && (
          <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 p-3.5" data-testid="password-error">
            <X className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
            <p className="text-[13px] text-red-900 leading-relaxed">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
          <PasswordField
            id="current-password"
            label="Current password"
            value={form.current}
            onChange={handleChange('current')}
            visible={show.current}
            onToggle={() => setShow({ ...show, current: !show.current })}
            autoComplete="current-password"
            testid="current-password-input"
          />
          <PasswordField
            id="new-password"
            label="New password"
            value={form.next}
            onChange={handleChange('next')}
            visible={show.next}
            onToggle={() => setShow({ ...show, next: !show.next })}
            autoComplete="new-password"
            testid="new-password-input"
            hint="At least 8 characters. Don't reuse your current password."
          />
          {form.next.length > 0 && (
            <StrengthMeter strength={strength} />
          )}
          <PasswordField
            id="confirm-password"
            label="Confirm new password"
            value={form.confirm}
            onChange={handleChange('confirm')}
            visible={show.confirm}
            onToggle={() => setShow({ ...show, confirm: !show.confirm })}
            autoComplete="new-password"
            testid="confirm-password-input"
            error={form.confirm.length > 0 && !matches ? 'Passwords do not match' : null}
          />

          <Button
            type="submit"
            disabled={!canSubmit}
            className="w-full sm:w-auto inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white px-5 h-11 text-[13px] font-semibold disabled:opacity-50"
            data-testid="submit-password-change"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Update password
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function PasswordField({ id, label, value, onChange, visible, onToggle, autoComplete, testid, hint, error }) {
  return (
    <div>
      <Label htmlFor={id} className="text-[12.5px] font-semibold text-slate-700">{label}</Label>
      <div className="relative mt-1.5">
        <Input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          className={`pr-11 h-11 text-[14px] ${error ? 'border-red-300 focus-visible:ring-red-300' : ''}`}
          data-testid={testid}
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-1 top-1/2 -translate-y-1/2 h-11 w-11 inline-flex items-center justify-center text-slate-400 hover:text-slate-700 rounded-md hover:bg-slate-100 transition-colors"
          aria-label={visible ? 'Hide password' : 'Show password'}
          tabIndex={-1}
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {hint && !error && <p className="mt-1.5 text-[11.5px] text-slate-500">{hint}</p>}
      {error && <p className="mt-1.5 text-[11.5px] text-red-600 font-medium">{error}</p>}
    </div>
  );
}

function StrengthMeter({ strength }) {
  const tiers = [
    { label: 'Too short', cls: 'bg-red-500', w: 'w-1/4' },
    { label: 'Weak', cls: 'bg-amber-500', w: 'w-2/4' },
    { label: 'Good', cls: 'bg-emerald-500', w: 'w-3/4' },
    { label: 'Strong', cls: 'bg-emerald-600', w: 'w-full' },
  ];
  const tier = tiers[Math.max(0, Math.min(strength, tiers.length - 1))];
  return (
    <div className="flex items-center gap-3" data-testid="password-strength">
      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-200 ${tier.cls} ${tier.w}`} />
      </div>
      <span className="text-[11.5px] font-semibold text-slate-600">{tier.label}</span>
    </div>
  );
}

function computeStrength(pw) {
  if (!pw || pw.length < 8) return 0;
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  if (pw.length >= 14) score += 1;
  return Math.min(score - 1, 3); // map to 0..3
}
